import { Box } from "@mui/material";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import styles from "../AiWebForm.styles";
import {
  changeChips,
  clockTime,
  contrastSuggestion,
  isAmber,
  orderWarnings,
  outcomeChangedNothing,
} from "../AiWebFormHelper";
import { ChatEntry } from "../AiWebFormTypesAndValidation";
import {
  AlertGlyph,
  CheckGlyph,
  EyeGlyph,
  FileGlyph,
  InfoGlyph,
  RefreshGlyph,
  SparkleGlyph,
  UserGlyph,
} from "./AiWebFormIcons";

const copy = strings.aiWebForm;

interface EntryProps {
  entry: ChatEntry;
  /** The version a "v3" chip opens in the preview. */
  onOpenVersion: (version: number) => void;
  /** Puts a suggestion or a kept message in the box. */
  onSuggest: (text: string) => void;
  busy: boolean;
}

const Byline = ({ entry }: { entry: Extract<ChatEntry, { kind: "user" }> }) => {
  const who = entry.byMe === false ? copy.colleague : copy.you;
  const time = clockTime(entry.at);
  return (
    <Box sx={styles.byline}>
      {entry.byMe === false && <UserGlyph />}
      <span>{time ? `${who} · ${time}` : who}</span>
    </Box>
  );
};

const UserEntry = ({ entry }: { entry: Extract<ChatEntry, { kind: "user" }> }) => (
  <Box sx={styles.userBlock}>
    <Byline entry={entry} />
    {entry.document && (
      <Box sx={styles.attachment}>
        <Box sx={styles.attachmentTile}>
          <FileGlyph />
        </Box>
        <Box sx={styles.attachmentText}>
          <Box sx={styles.attachmentName}>{entry.document.name}</Box>
          <Box sx={styles.attachmentMeta}>{entry.document.meta}</Box>
        </Box>
      </Box>
    )}
    <Box sx={styles.userBubble(entry.byMe !== false)}>
      {entry.text}
      {entry.styleLine && (
        <Box component="span" sx={styles.styleLine}>
          {entry.styleLine}
        </Box>
      )}
    </Box>
  </Box>
);

const AssistantEntry = ({ entry, onOpenVersion, onSuggest, busy }: EntryProps & { entry: Extract<ChatEntry, { kind: "assistant" }> }) => {
  const chips = entry.outcome === "changed" || (entry.outcome === null && entry.changes) ? changeChips(entry.changes) : [];
  const warnings = orderWarnings(entry.warnings);
  const showVersion = entry.designVersion != null && !outcomeChangedNothing(entry.outcome);
  return (
    <Box sx={styles.replyRow}>
      <Box sx={styles.replyTile} aria-hidden="true">
        <SparkleGlyph />
      </Box>
      <Box sx={styles.replyColumn}>
        <Box sx={styles.replyBubble}>{entry.text}</Box>
        {chips.length > 0 && (
          <Box sx={styles.changed}>
            <Box component="span" sx={styles.changedLabel}>
              {copy.whatChanged}
            </Box>
            {chips.map((chip) => (
              <Box key={chip} component="span" sx={styles.changeChip}>
                <CheckGlyph />
                {chip}
              </Box>
            ))}
          </Box>
        )}
        {warnings.map((warning) =>
          isAmber(warning) ? (
            <Box key={`${warning.code}-${warning.message}`} sx={{ display: "contents" }}>
              <Box sx={styles.warning} role="note">
                <AlertGlyph />
                <span>{warning.message}</span>
              </Box>
              {warning.code === "contrast_below_aa" && (
                <Box
                  component="button"
                  type="button"
                  sx={styles.suggestion}
                  disabled={busy}
                  onClick={() => onSuggest(contrastSuggestion(warning))}
                >
                  <SparkleGlyph />
                  {contrastSuggestion(warning)}
                </Box>
              )}
            </Box>
          ) : (
            <Box key={`${warning.code}-${warning.message}`} sx={styles.infoNotice} role="note">
              <InfoGlyph />
              <span>{warning.message}</span>
            </Box>
          ),
        )}
        {entry.retryText && (
          <Box
            component="button"
            type="button"
            sx={styles.suggestion}
            disabled={busy}
            onClick={() => onSuggest(entry.retryText as string)}
          >
            <RefreshGlyph />
            {copy.tryAgain}
          </Box>
        )}
        {showVersion ? (
          <Box sx={styles.versionLine}>
            <Box
              component="button"
              type="button"
              sx={styles.versionChip}
              onClick={() => onOpenVersion(entry.designVersion as number)}
              aria-label={`${copy.preview} v${entry.designVersion}`}
            >
              <EyeGlyph />v{entry.designVersion}
            </Box>
            <span>
              {entry.designVersion === 1
                ? copy.createdFromPdf
                : fillCopy(copy.afterMessage, { turn: entry.turn ?? "" })}
            </span>
          </Box>
        ) : (
          entry.designVersion != null && (
            <Box sx={styles.note}>{fillCopy(copy.noChange, { version: entry.designVersion })}</Box>
          )
        )}
      </Box>
    </Box>
  );
};

export const AiChatEntry = (props: EntryProps) => {
  const { entry } = props;
  if (entry.kind === "system") {
    const time = clockTime(entry.at);
    return <Box sx={styles.systemLine}>{time ? `${entry.text} · ${time}` : entry.text}</Box>;
  }
  if (entry.kind === "user") return <UserEntry entry={entry} />;
  return <AssistantEntry {...props} entry={entry} />;
};

/** The start's progress: the steps advance on a clock, the start itself is one call. */
export const AiStartingChecklist = ({ step, styleText }: { step: number; styleText?: string }) => {
  const steps = [copy.stepRead, copy.stepBuild, styleText ? fillCopy(copy.stepStyle, { style: styleText }) : copy.stepLook];
  return (
    <Box sx={styles.replyRow}>
      <Box sx={styles.replyTile} aria-hidden="true">
        <SparkleGlyph />
      </Box>
      <Box sx={styles.replyColumn}>
        <Box sx={styles.replyBubble}>
          <Box sx={styles.checklist} role="list">
            {steps.map((label, index) => {
              const state = index < step ? "done" : index === step ? "active" : "todo";
              return (
                <Box key={label} sx={styles.step(state)} role="listitem">
                  <Box sx={styles.stepMark(state)} aria-hidden="true">
                    {state === "done" && <CheckGlyph />}
                    {state === "active" && (
                      <Box sx={styles.dots(4)}>
                        <span />
                        <span />
                        <span />
                      </Box>
                    )}
                  </Box>
                  {label}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box sx={styles.note}>{copy.startingNote}</Box>
      </Box>
    </Box>
  );
};

export const AiWorking = () => (
  <Box sx={styles.replyRow} role="status">
    <Box sx={styles.replyTile} aria-hidden="true">
      <SparkleGlyph />
    </Box>
    <Box sx={styles.replyColumn}>
      <Box sx={styles.replyBubble}>
        <Box sx={styles.typing}>
          <Box sx={styles.dots(6)} aria-hidden="true">
            <span />
            <span />
            <span />
          </Box>
          {copy.working}
        </Box>
      </Box>
      <Box sx={styles.note}>{copy.workingNote}</Box>
    </Box>
  </Box>
);
